import { useMemo } from 'react';

import {
    IOfferEventDetailFragment,
    IOfferEventSubscriptionStatus,
} from 'graphql/types';

export const useOfferEventProgress = (
    offerEvent: Pick<
        IOfferEventDetailFragment,
        | 'certificate'
        | 'portfolioItem'
        | 'accessTrainings'
        | 'accessExternalContents'
        | 'accessCourses'
        | 'accessScorms'
        | 'currentUserEnrollment'
        | 'certificatePortfolioItem'
    >
) =>
    useMemo(() => {
        const {
            portfolioItem,
            certificate,
            accessTrainings,
            accessExternalContents,
            accessCourses,
            accessScorms,
            currentUserEnrollment,
            certificatePortfolioItem,
        } = offerEvent;

        const hasExternalCertificate = !!certificate?.isExternalCertificate;

        // Only show progress to users which have an enrollment with status enrolled or completed
        if (
            !currentUserEnrollment ||
            ![
                IOfferEventSubscriptionStatus.Enrolled,
                IOfferEventSubscriptionStatus.Completed,
            ].includes(currentUserEnrollment.status)
        ) {
            return {
                hasProgressMaterial: false,
                showScore: false,
                showProgress: false,
                showProgressSection: false,
            };
        }

        const { progress, score } = portfolioItem || {};

        const hasProgressMaterial =
            !!accessTrainings.edges.length ||
            !!accessExternalContents.edges.length ||
            !!accessCourses.edges.length ||
            !!accessScorms.edges.length;

        const showScore = !!(progress === 1 && score && hasProgressMaterial);
        const showProgress = progress !== undefined && hasProgressMaterial;
        const showProgressSection =
            hasExternalCertificate ||
            (!!certificate && !!certificatePortfolioItem) ||
            showScore ||
            showProgress;

        return {
            hasProgressMaterial,
            showScore,
            showProgress,
            showProgressSection,
        };
    }, [
        offerEvent.certificate,
        offerEvent.portfolioItem,
        offerEvent.accessTrainings,
        offerEvent.accessExternalContents,
        offerEvent.accessCourses,
        offerEvent.accessScorms,
        offerEvent.currentUserEnrollment,
        offerEvent.certificatePortfolioItem,
    ]);
